import React, { useState, useEffect, useRef } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import './SignUp.css';
import { addUserEntry } from "./firebaseConfig"; // Stellen Sie sicher, dass Sie den richtigen Pfad zu Ihrer Konfigurationsdatei angeben
import GenerativeOrbAnimation from './Pages/GenerativeOrbAnimation.js';
import LoginShowcase from './LoginShowcase';

// DSGVO/GDPR-konforme Passwortrichtlinie (orientiert an BSI-Empfehlungen für DE/AT).
// Gilt nur für die Neuregistrierung – bestehende Accounts/Logins bleiben unberührt.
const PASSWORD_MIN_LENGTH = 10;

const getPasswordChecks = (pw = "") => ({
  length: pw.length >= PASSWORD_MIN_LENGTH,
  uppercase: /[A-Z]/.test(pw),
  lowercase: /[a-z]/.test(pw),
  number: /[0-9]/.test(pw),
  special: /[^A-Za-z0-9]/.test(pw),
});

const isStrongPassword = (pw = "") => {
  const checks = getPasswordChecks(pw);
  return Object.values(checks).every(Boolean);
};

const EU_STAR_POINTS = "0,-1 0.235,-0.324 0.951,-0.309 0.38,0.124 0.588,0.809 0,0.4 -0.588,0.809 -0.38,0.124 -0.951,-0.309 -0.235,-0.324";

function EuFlag() {
  const radius = 7.1;
  return (
    <svg className="login-about-eu" viewBox="0 0 36 24" aria-hidden="true" focusable="false">
      <rect width="36" height="24" rx="2" fill="#003399" />
      <g fill="#FFCC00" transform="translate(18 12)">
        {Array.from({ length: 12 }, (_, i) => {
          const angle = ((i * 30) - 90) * Math.PI / 180;
          const x = (Math.cos(angle) * radius).toFixed(2);
          const y = (Math.sin(angle) * radius).toFixed(2);
          return (
            <polygon
              key={i}
              points={EU_STAR_POINTS}
              transform={`translate(${x} ${y}) scale(1.45)`}
            />
          );
        })}
      </g>
    </svg>
  );
}

function SignUp({ isAuthenticated, setIsAuthenticated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [message, setMessage] = useState("");
  const [showSignIn, setShowSignIn] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const auth = getAuth();
  const [isValid, setIsValid] = useState(false); 
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordConfirmationTouched, setPasswordConfirmationTouched] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPasswordHints, setShowPasswordHints] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const verificationTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const debouncedEmail = useDebounce(email, 600);
  const debouncedPassword = useDebounce(password, 600);
  const debouncedPasswordConfirmation = useDebounce(passwordConfirmation, 600);

  const handleEmailBlur = () => {
    setEmailTouched(true);
  };

  const handlePasswordBlur = () => {
    setPasswordTouched(true);
  };

  const handlePasswordConfirmationBlur = () => {
    setPasswordConfirmationTouched(true);
  };

  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
  
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
  
    return debouncedValue;
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!isTimerActive) {
        if (user) {
          if (user.emailVerified) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      }
    });
    
    return () => unsubscribe();
  }, [isTimerActive, auth, setIsAuthenticated]);

  useEffect(() => {
    return () => {
      if (verificationTimeoutRef.current) clearTimeout(verificationTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const validateInputs = () => {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

      if (emailTouched && debouncedEmail && !emailRegex.test(debouncedEmail)) {
        setMessage("Please type in a valid e-mail address.");
        return false;
      }

      // Login (bestehende Accounts): unverändert – nur ein nicht-leeres Passwort nötig.
      if (showSignIn) {
        setMessage("");
        return true;
      }

      // Neuregistrierung: DSGVO/BSI-konforme Passwortstärke erzwingen.
      if (passwordTouched && debouncedPassword && !isStrongPassword(debouncedPassword)) {
        setMessage(`Your password must be at least ${PASSWORD_MIN_LENGTH} characters and include an uppercase letter, a lowercase letter, a number and a special character.`);
        return false;
      }

      if (passwordConfirmationTouched && debouncedPassword && debouncedPasswordConfirmation && debouncedPassword !== debouncedPasswordConfirmation) {
        setMessage("Passwords do not match.");
        return false;
      }

      setMessage(""); 
      return true;
    };

    setIsValid(validateInputs());
  }, [debouncedEmail, debouncedPassword, debouncedPasswordConfirmation, emailTouched, passwordTouched, passwordConfirmationTouched, showSignIn]);

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!isStrongPassword(password)) {
      setMessage(`Your password must be at least ${PASSWORD_MIN_LENGTH} characters and include an uppercase letter, a lowercase letter, a number and a special character.`);
      return;
    }

    if (password !== passwordConfirmation) {
      setMessage("Passwords do not match.");
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      try {
        await addUserEntry(userCredential.user.uid);
      } catch (dbError) {
        console.error('Database entry failed (will retry on next login):', dbError);
      }
      
      await sendEmailVerification(userCredential.user);
      setMessage("");

      setIsTimerActive(true);
      setShowVerificationScreen(true);
      if (verificationTimeoutRef.current) clearTimeout(verificationTimeoutRef.current);
      verificationTimeoutRef.current = setTimeout(() => {
        setIsTimerActive(false);
        auth.signOut();
      }, 10000);
    } catch (error) {
      setMessage(getFriendlyErrorMessage(error));
    }
  };

  // Wird vom "Go to Log In"-Button auf dem Verifizierungs-Screen aufgerufen.
  // Prüft live, ob die E-Mail bereits bestätigt wurde:
  //  - falls ja  -> direkt in den Account (keine erneute Eingabe nötig)
  //  - falls nein -> Login-Formular mit bereits ausgefüllten Daten
  const handleGoToLogin = async () => {
    if (verificationTimeoutRef.current) {
      clearTimeout(verificationTimeoutRef.current);
      verificationTimeoutRef.current = null;
    }
    setIsCheckingVerification(true);
    setIsTimerActive(false);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await userCredential.user.reload();

      if (auth.currentUser && auth.currentUser.emailVerified) {
        setMessage("");
        setShowVerificationScreen(false);
        setIsAuthenticated(true);
        navigate('/dashboard');
        return;
      }

      // Noch nicht bestätigt -> zurück zum Login, Daten bleiben vorausgefüllt.
      await auth.signOut();
      setShowVerificationScreen(false);
      setShowSignIn(true);
      setMessage("Please confirm your email first, then log in.");
    } catch (error) {
      // Bei einem Fehler ebenfalls auf das Login-Formular wechseln (Daten bleiben erhalten).
      setShowVerificationScreen(false);
      setShowSignIn(true);
      setMessage(getFriendlyErrorMessage(error));
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (userCredential.user.emailVerified) {
        setMessage("Erfolgreich angemeldet!");
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else {
        setMessage("Bitte bestätige deine E-Mail-Adresse, bevor du dich anmeldest.");
        setShowVerificationScreen(true);
      }
    } catch (error) {
      setMessage(getFriendlyErrorMessage(error));
    }
  };

  const handleGoogleSignup = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setMessage("Successfully signed up with Google!");
    } catch (error) {
      setMessage(getFriendlyErrorMessage(error));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset link sent to your email!");
    } catch (error) {
      setMessage(getFriendlyErrorMessage(error));
    }
  };

  function getFriendlyErrorMessage(error) {
    console.error('Firebase Auth Error:', error.code, error.message);
    
    const firebaseErrorMapping = {
      'auth/invalid-email': 'This e-mail does not seem valid.',
      'auth/user-disabled': 'Your account has been deactivated.',
      'auth/user-not-found': 'No account found. Did you mean Sign Up?',
      'auth/wrong-password': 'Sorry, wrong password.',
      'auth/email-already-in-use': 'This e-mail is already registered. Try logging in instead.',
      'auth/weak-password': 'Your password is too weak. Please use at least 8 characters.',
      'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
      'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      'auth/invalid-credential': 'Invalid email or password. Please try again.',
      'auth/invalid-login-credentials': 'Invalid email or password. Please try again.',
      'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
      'auth/account-exists-with-different-credential': 'An account with this e-mail already exists. Try a different sign-in method.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
      'auth/requires-recent-login': 'Please log in again to complete this action.',
      'auth/internal-error': 'A temporary error occurred. Please try again.',
    };

    if (!firebaseErrorMapping[error.code]) {
      console.warn('Unmapped Firebase error code:', error.code);
    }

    return firebaseErrorMapping[error.code] || `An unexpected error occurred (${error.code || 'unknown'}). Please try again.`;
  }

  return (
    <div className="login login--split">
      <GenerativeOrbAnimation />
      <div className="login-split">
      <main className="login-pane">
      <a className="login-brand" href="https://www.bonusclub.io" target="_blank" rel="noopener noreferrer">
        <img src="./img/BonusClub_Logo.webp" alt="" />
        <span>BonusClub</span>
      </a>
      <div className="login-container">
        {showVerificationScreen ? (
          <div className="verify_mail_container">
            <div className="verify-icon">✉️</div>
            <h1>Check your inbox</h1>
            <p>We've sent a verification link to</p>
            {auth.currentUser && <div className="verify-email-badge">{auth.currentUser.email}</div>}
            <p>Please verify your email to get started.</p>
            <button className="verify-btn" onClick={handleGoToLogin} disabled={isCheckingVerification}>
              {isCheckingVerification ? "Checking…" : "Continue"}
            </button>
          </div>
        ) : showSignIn ? (
          <div className="signup-screen">
            <div className="toggle-buttons">
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <button 
                  className={`toggle_button_1 ${showSignIn ? 'active' : ''}`}
                  onClick={() => setShowSignIn(true)}
                >
                  Log In
                </button>
                <button 
                  className={`toggle_button_1 ${!showSignIn ? 'active' : ''}`}
                  onClick={() => setShowSignIn(false)}
                >
                  Sign Up
                </button>
              </div>
            </div>
            <img className="login-logo" src="./img/BonusClub.png" alt="BonusClub" />
            <p>Log in to your account</p>
            <form onSubmit={handleSignIn}>
              <div>
                <label className="login-label">Email:</label>
                <div className={`login-input-wrapper ${email ? "has-content" : "login-input-wrapper-passive"}`}>
                  <input className="login-input" type="email" placeholder='Your Email' value={email} onChange={(e) => setEmail(e.target.value)} onBlur={handleEmailBlur} required />
                  <img className="mail-icon" src="./img/mail.svg" alt="" />
                </div>
              </div>
              <div>
                <label className="login-label">Password:</label>
                <div className={`login-input-wrapper ${password ? "has-content" : "login-input-wrapper-passive"}`}>
                  <input className="login-input" type="password" placeholder='Your Password' value={password} onChange={(e) => setPassword(e.target.value)} onBlur={handlePasswordBlur} required />
                  <img className="lock-icon" src="./img/padlock.svg" alt="" />
                </div>
                <Link className="login-btn-4" to="/forgot-password">Forgot?</Link>
              </div>
              <button className={`login-btn-1 ${isValid ? "active-btn" : ""}`} type="submit" disabled={!isValid}>Log in</button>
            </form>
            <button className="login-btn-2" onClick={handleGoogleSignup}>
              <img className="google_logo" src="./img/google.webp" alt="" style={{marginRight: '10px'}}/>
              <p>Continue with Google</p>
            </button>
          </div>
        ) : showForgotPassword ? (
          <div className="signup-screen">
            <h2 className="login-header">Forgot Password</h2>
            <form onSubmit={handlePasswordReset}>
              <div>
                <label className="login-label">Email:</label>
                <input className="login-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <button className="login-btn" type="submit">Reset Password</button>
            </form>
            <button className="login-btn" onClick={() => setShowForgotPassword(false)}>Back to Sign In</button>
          </div>
        ) : (
          <div className="signup-screen">
            <div className="toggle-buttons">
              <div>
                <button 
                  className={`toggle_button_1 ${showSignIn ? 'active' : ''}`}
                  onClick={() => setShowSignIn(true)}
                >
                  Log In
                </button>
                <button 
                  className={`toggle_button_1 ${!showSignIn ? 'active' : ''}`}
                  onClick={() => setShowSignIn(false)}
                >
                  Sign Up
                </button>
              </div>
            </div>
            <img className="login-logo" src="./img/BonusClub.png" alt="BonusClub" />
            <p>Create your Account</p>
            <form onSubmit={handleSignUp}>
              <div>
                <label className="login-label">Email:</label>
                <div className={`login-input-wrapper ${email ? "has-content" : "login-input-wrapper-passive"}`}>
                  <input className="login-input" type="email" placeholder='Set your E-Mail' value={email} onChange={(e) => setEmail(e.target.value)} onBlur={handleEmailBlur} required />
                  <img className="mail-icon" src="./img/mail.svg" alt="" />
                </div>
              </div>
              <div>
                <label className="login-label">Password:</label>
                <div className={`login-input-wrapper ${password ? "has-content" : "login-input-wrapper-passive"}`}>
                  <input className="login-input" type="password" placeholder='Create a password' value={password} onChange={(e) => setPassword(e.target.value)} onBlur={handlePasswordBlur} required />
                  <img className="lock-icon" src="./img/padlock.svg" alt="" />
                </div>
                <div className={`login-input-wrapper ${passwordConfirmation ? "has-content" : "login-input-wrapper-passive"}`}>
                  <input className="login-input" type="password" placeholder='Confirm password' value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} onBlur={handlePasswordConfirmationBlur} required />
                  <img className="lock-icon" src="./img/padlock.svg" alt="" />
                </div>
                {(() => {
                  // Debounced, damit der User beim Tippen nicht gestört wird –
                  // der Hinweis erscheint nur, wenn nach kurzer Pause noch etwas fehlt.
                  const checks = getPasswordChecks(debouncedPassword);
                  const requirements = [
                    { key: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters` },
                    { key: 'uppercase', label: 'One uppercase letter' },
                    { key: 'lowercase', label: 'One lowercase letter' },
                    { key: 'number', label: 'One number' },
                    { key: 'special', label: 'One special character' },
                  ];
                  const metCount = requirements.filter((req) => checks[req.key]).length;
                  const allMet = metCount === requirements.length;

                  // Kein Platzverbrauch, solange leer oder bereits alles erfüllt.
                  if (!debouncedPassword || allMet) return null;

                  return (
                    <div className="password-hint">
                      <button
                        type="button"
                        className="password-hint-header"
                        onClick={() => setShowPasswordHints((v) => !v)}
                      >
                        <span className="password-hint-icon">!</span>
                        <span className="password-hint-label">
                          Password requirements ({metCount}/{requirements.length})
                        </span>
                        <span className={`password-hint-chevron ${showPasswordHints ? 'open' : ''}`}>⌄</span>
                      </button>
                      {showPasswordHints && (
                        <ul className="password-requirements">
                          {requirements.map((req) => (
                            <li key={req.key} className={checks[req.key] ? 'met' : 'unmet'}>
                              <span className="password-req-icon">{checks[req.key] ? '✓' : '○'}</span>
                              {req.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="terms-toggle" onClick={() => setTermsAccepted(!termsAccepted)}>
                <div className={`terms-toggle-switch ${termsAccepted ? 'active' : ''}`}>
                  <div className="terms-toggle-knob" />
                </div>
                <span>I agree to the <a href="https://www.bonusclub.io/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Terms</a> & <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Privacy Policy</a></span>
              </div>
              <button className={`login-btn-1 ${isValid && termsAccepted ? "active-btn" : ""}`} type="submit" disabled={!isValid || !termsAccepted}>Create Account</button>
            </form>
            <button className="login-btn-2" onClick={handleGoogleSignup}>
              <img className="google_logo" src="./img/google.webp" alt="" style={{marginRight: '10px'}}/>
              <p>Continue with Google</p>
            </button>
          </div>
        )}
        <p className={`login-message ${message.includes("Erfolg") ? "success" : "error"}`}>{message}</p>
      </div>
      </main>

      <aside className="login-about" aria-labelledby="login-about-title">
        <LoginShowcase />

        <div className="login-about-body">
        <p className="login-about-eyebrow">About BonusClub</p>
        <h1 id="login-about-title">
          The operating system for <span className="login-about-accent">outbound outreach.</span>
        </h1>
        <p className="login-about-lead">
          BonusClub is a B2B sales platform by BonusClub LLC. It helps companies find the right
          decision-makers, write personalized emails with AI and follow up in a structured way.
          A human reviews and approves every message before it is sent.
        </p>

        <ol className="login-about-features">
          <li>
            <strong>Lead research</strong>
            <span>Company insights and decision-makers from publicly available sources.</span>
          </li>
          <li>
            <strong>Personalized outreach</strong>
            <span>AI drafts every email in your voice. You review, edit and approve.</span>
          </li>
          <li>
            <strong>Structured follow-up</strong>
            <span>Inbox, pipeline and tasks in one place, so no lead falls through the cracks.</span>
          </li>
          <li>
            <strong className="login-about-gdpr">
              GDPR Friendly
              <EuFlag />
            </strong>
            <span>European servers, a tamper-proof audit trail and instant data deletion.</span>
          </li>
        </ol>

        <div className="login-about-google">
          <strong>Signing in with Google</strong>
          <p>
            If you continue with Google, BonusClub only receives your name, email address and
            profile picture to create and secure your account. We never access your Gmail,
            Drive, Calendar or contacts, and we never sell or share your data.
            Details are in our <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </div>

        <div className="login-about-footer">
          <div className="signup-legal-links">
            <a href="https://www.bonusclub.io" target="_blank" rel="noopener noreferrer">bonusclub.io</a>
            <Link to="/privacy">Privacy Policy</Link>
            <a href="https://www.bonusclub.io/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            <Link to="/impressum">Imprint</Link>
          </div>
          <p className="login-about-company">© {new Date().getFullYear()} BonusClub LLC · St. Petersburg, FL, USA</p>
        </div>
        </div>
      </aside>
      </div>
    </div>
  );
}

export default SignUp;
